import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.refs.searchInput.addEventListener('focus', this.#handleSearchFocus, { signal });
    this.addEventListener('mouseover', this.#handleCollectionPreviewEvent, { signal });
    this.addEventListener('mouseout', this.#handleCollectionPreviewMouseOut, { signal });
    this.addEventListener('focusin', this.#handleCollectionPreviewEvent, { signal });
    this.addEventListener('focusout', this.#handleCollectionPreviewFocusOut, { signal });

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #handleSearchFocus = () => {
    const isEmptySearch = this.refs.searchInput.value.trim().length === 0;

    if (isEmptySearch && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState(true);
    }

    if (isEmptySearch) {
      this.#renderRecentlyViewedProductsFromStorage();
    }
  };

  /**
   * Swaps the product panel only while a collection suggestion is actively previewed.
   * @param {Event} event
   */
  #handleCollectionPreviewEvent = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const previewTrigger = target.closest('[data-collection-preview-template]');
    const productsPanel = this.querySelector('[data-hoover-search-products-panel]');
    if (!(productsPanel instanceof HTMLElement)) return;

    if (!(previewTrigger instanceof HTMLElement)) return;

    const templateId = previewTrigger.dataset.collectionPreviewTemplate;
    if (!templateId) return;

    const template = this.querySelector(`#${CSS.escape(templateId)}`);
    if (!(template instanceof HTMLTemplateElement)) return;

    productsPanel.replaceChildren(template.content.cloneNode(true));
  };

  /**
   * @param {MouseEvent} event
   */
  #handleCollectionPreviewMouseOut = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const previewTrigger = target.closest('[data-collection-preview-template]');
    if (!(previewTrigger instanceof HTMLElement)) return;
    if (event.relatedTarget instanceof Node && previewTrigger.contains(event.relatedTarget)) return;

    const productsPanel = this.querySelector('[data-hoover-search-products-panel]');
    if (productsPanel instanceof HTMLElement) {
      this.#restoreDefaultProductPreview(productsPanel);
    }
  };

  /**
   * @param {FocusEvent} event
   */
  #handleCollectionPreviewFocusOut = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const previewTrigger = target.closest('[data-collection-preview-template]');
    if (!(previewTrigger instanceof HTMLElement)) return;
    if (event.relatedTarget instanceof Node && previewTrigger.contains(event.relatedTarget)) return;

    const productsPanel = this.querySelector('[data-hoover-search-products-panel]');
    if (productsPanel instanceof HTMLElement) {
      this.#restoreDefaultProductPreview(productsPanel);
    }
  };

  /**
   * @param {HTMLElement} productsPanel
   */
  #restoreDefaultProductPreview(productsPanel) {
    const defaultTemplate = this.querySelector('[data-default-product-preview-template]');
    if (!(defaultTemplate instanceof HTMLTemplateElement)) return;

    productsPanel.replaceChildren(defaultTemplate.content.cloneNode(true));
  }

  #loadEmptyState(force = false) {
    if (this.#emptyStateLoaded && !force) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });

    if (activeItem && this.#isKeyboardNavigation) {
      this.refs.searchInput.focus();
    }
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  get #firstResultFocusable() {
    return (
      this.refs.predictiveSearchResults.querySelector(
        '[ref="resultsItems[]"] a[href], .predictive-search-results__card a[href], .predictive-search-results__card--query a[href]'
      ) || null
    );
  }

  get #resultFocusables() {
    return Array.from(
      this.refs.predictiveSearchResults.querySelectorAll(
        '[ref="resultsItems[]"] a[href], .predictive-search-results__card a[href], .predictive-search-results__card--query a[href]'
      )
    ).filter((item) => item instanceof HTMLElement);
  }

  get #lastResultFocusable() {
    return this.#resultFocusables.at(-1) || null;
  }

  get #exitFocusable() {
    const candidates = [
      this.querySelector('.predictive-search__reset-button:not([hidden])'),
      this.querySelector('.hoover-header__mobile-search-close'),
      this.querySelector('[ref="closeModalButton"]'),
    ];

    return (
      candidates.find(
        (candidate) =>
          candidate instanceof HTMLElement &&
          !candidate.hasAttribute('hidden') &&
          candidate.getAttribute('aria-hidden') !== 'true'
      ) || null
    );
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (
      event.key === 'Tab' &&
      !event.shiftKey &&
      event.target === this.refs.searchInput &&
      this.#firstResultFocusable instanceof HTMLElement
    ) {
      event.preventDefault();
      this.#isKeyboardNavigation = true;
      this.#firstResultFocusable.focus();
      return;
    }

    if (
      event.key === 'Tab' &&
      event.shiftKey &&
      event.target instanceof HTMLElement &&
      event.target === this.#firstResultFocusable
    ) {
      event.preventDefault();
      this.refs.searchInput.focus();
      return;
    }

    if (
      event.key === 'Tab' &&
      !event.shiftKey &&
      event.target instanceof HTMLElement &&
      event.target === this.#lastResultFocusable &&
      this.#exitFocusable instanceof HTMLElement
    ) {
      event.preventDefault();
      this.#exitFocusable.focus();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          searchUrl.searchParams.set('options[prefix]', 'last');
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    // If the input is not a text input (like using the Escape key), don't search.
    // Browser-native clear controls can emit an input event without inputType, so
    // the empty state check above must run first.
    if (!event.inputType) return;

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,variants.title,variants.sku');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);
        this.#logProductResults(predictiveSearchResults, searchTerm);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    const viewedProductHandles = RecentlyViewed.getProductHandles?.() || [];
    if (viewedProducts.length === 0 && viewedProductHandles.length === 0) return null;

    if (viewedProducts.length > 0) {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');

      const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      const parsedMarkup = new DOMParser().parseFromString(markup, 'text/html');
      const productContainer = parsedMarkup.getElementById('predictive-search-products');
      const hasProducts = !!productContainer?.querySelector(
        '.hoover-search-recent-product, .predictive-search-results__card'
      );

      if (hasProducts) return markup;
    }

    return this.#getRecentlyViewedProductsJsonMarkup(viewedProductHandles);
  }

  /**
   * @param {string[]} handles
   * @returns {Promise<string | null>}
   */
  async #getRecentlyViewedProductsJsonMarkup(handles) {
    const rootUrl = window.Shopify?.routes?.root || Theme.routes.root_url || '/';
    const products = (
      await Promise.all(
        handles.map(async (handle) => {
          if (!handle) return null;
          try {
            const response = await fetch(`${rootUrl}products/${encodeURIComponent(handle)}.js`);
            if (!response.ok) return null;
            return response.json();
          } catch (_error) {
            return null;
          }
        })
      )
    ).filter(Boolean);

    if (products.length === 0) return null;

      const productItems = products
      .slice(0, 3)
      .map((product, index) => {
        const title = this.#escapeHtml(product.title || '');
        const handle = this.#escapeAttribute(product.handle || handles[index] || '');
        const image = product.featured_image
          ? `<img class="hoover-search-recent-product__image" src="${this.#escapeAttribute(product.featured_image)}" alt="${title}" loading="lazy">`
          : '';

        return `<li ref="recentlyViewedItems[]"><a class="hoover-search-recent-product" href="${rootUrl}products/${handle}">${image}<span>${title}</span></a></li>`;
      })
      .join('');

    return `<div id="predictive-search-products" class="hoover-search-empty__products"><div class="recently-viewed-wrapper"><h4 class="hoover-search__eyebrow" ref="recentlyViewedTitle[]">Recently viewed</h4><ul class="hoover-search-recent-list list-unstyled">${productItems}</ul></div></div>`;
  }

  async #renderRecentlyViewedProductsFromStorage() {
    const handles = RecentlyViewed.getProductHandles?.() || [];
    if (handles.length === 0) return;

    const markup = await this.#getRecentlyViewedProductsJsonMarkup(handles);
    if (!markup) return;

    const recentlyViewedProductsHtml = new DOMParser().parseFromString(markup, 'text/html').getElementById('predictive-search-products');
    const collectionElement = this.refs.predictiveSearchResults.querySelector('#predictive-search-products');
    if (!recentlyViewedProductsHtml || !collectionElement) return;

    collectionElement.replaceChildren(...recentlyViewedProductsHtml.children);
  }

  /** @param {string} value */
  #escapeHtml(value) {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML;
  }

  /** @param {string} value */
  #escapeAttribute(value) {
    return this.#escapeHtml(value).replaceAll('"', '&quot;');
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  /**
   * Logs predictive search product data so result prioritization can be inspected in DevTools.
   * @param {HTMLElement} predictiveSearchResults
   * @param {string} searchTerm
   */
  #logProductResults(predictiveSearchResults, searchTerm) {
    const dataElement = predictiveSearchResults.querySelector('[data-hoover-search-product-results]');
    if (!dataElement?.textContent) return;

    try {
      const results = JSON.parse(dataElement.textContent);
      console.log('[Hoover predictive search products]', searchTerm, results.products, results);
    } catch (error) {
      console.warn('[Hoover predictive search products] Unable to parse product results', error);
    }
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = this.dataset.emptySectionId || 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptyDocument = new DOMParser().parseFromString(emptySectionMarkup, 'text/html');
    const parsedEmptySectionMarkup =
      parsedEmptyDocument.querySelector('.predictive-search-empty-section') ||
      parsedEmptyDocument.getElementById(searchInput.getAttribute('aria-controls') || 'predictive-search-results') ||
      parsedEmptyDocument.querySelector('.predictive-search-dropdown');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();
    const viewedProductHandles = RecentlyViewed.getProductHandles?.() || [];

    if (viewedProducts.length > 0 || viewedProductHandles.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) {
        morph(predictiveSearchResults, parsedEmptySectionMarkup);
        this.#resetScrollPositions();
        return;
      }

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) {
        morph(predictiveSearchResults, parsedEmptySectionMarkup);
        this.#resetScrollPositions();
        return;
      }

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) {
        morph(predictiveSearchResults, parsedEmptySectionMarkup);
        this.#resetScrollPositions();
        return;
      }
      if (collectionElement.hasAttribute('data-replace-recently-viewed')) {
        collectionElement.replaceChildren(...recentlyViewedProductsHtml.children);
      } else {
        collectionElement.prepend(...recentlyViewedProductsHtml.children);
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
